import { Fragment, useMemo } from "react";
import { calculateCombinedStandings, calculateBracketSize } from "../utils/standings";

export default function PoolStandings({ pools, bouts, showBracketCutoff = true, advancingCount = 0 }) {
  const standings = useMemo(() => {
    if (!pools?.length || !bouts?.some(b => b.status === "confirmed" && b.confirmedResult)) return [];
    return calculateCombinedStandings(pools, bouts);
  }, [pools, bouts]);
  const bracketInfo = useMemo(() => showBracketCutoff && standings.length ? calculateBracketSize(standings.length) : null, [standings, showBracketCutoff]);
  const totalPoolBouts = bouts.filter(b => pools.some(p => p.name === b.poolName)).length;
  const confirmedPoolBouts = bouts.filter(b => b.status === "confirmed" && pools.some(p => p.name === b.poolName)).length;
  const allDone = totalPoolBouts > 0 && confirmedPoolBouts === totalPoolBouts;
  if (!standings.length) return null;

  return <div>
    <div className="text-right text-xs text-gray-300 mb-2">
      {confirmedPoolBouts}/{totalPoolBouts} bouts scored
      {allDone && <span className="text-green-action-text ml-2">✓ Complete</span>}
    </div>
    <div className="bg-dark-700 border border-dark-500 rounded-xl overflow-x-auto">
      <table className="w-full min-w-[370px] text-sm">
        <caption className="sr-only">Pool standings</caption>
        <thead><tr className="border-b border-dark-500 text-xs text-gray-300">
          <th scope="col" className="p-2 text-left">Rank</th>
          <th scope="col" className="p-2 text-left">Fighter</th>
          <th scope="col" className="p-2 text-center">W-T-L</th>
          <th scope="col" className="p-2 text-center">P/I</th>
          <th scope="col" className="p-2 text-center">P/R</th>
          {pools.length > 1 && <th scope="col" className="p-2 text-center">Pool</th>}
        </tr></thead>
        <tbody>{standings.map((s, i) => {
          const highlighted = (bracketInfo && s.rank <= bracketInfo.advancing) || (advancingCount > 0 && i < advancingCount);
          const isCutoffLine = bracketInfo && s.rank === bracketInfo.advancing && i < standings.length - 1 && standings[i + 1].rank > bracketInfo.advancing;
          const isAdvancedCutoff = !isCutoffLine && advancingCount > 0 && i === advancingCount - 1 && i < standings.length - 1;
          return <Fragment key={s.name}>
            <tr className="border-b border-dark-500/30" style={{ background: highlighted ? "#1a2a1a" : undefined }}>
              <td className="p-2 font-display font-extrabold" style={{ color: s.needsTiebreaker ? "#FFB74D" : highlighted ? "#81C784" : "#bbb" }}>{s.rank}</td>
              <th scope="row" className="p-2 text-left font-semibold text-gray-200">{s.name}{s.needsTiebreaker && <span className="text-xs text-yellow-400 ml-1.5">⚠ tie</span>}</th>
              <td className="p-2 text-center text-gray-300">{s.wins}-{s.ties}-{s.losses}</td>
              <td className="p-2 text-center font-display font-extrabold" style={{ color: highlighted ? "#81C784" : "#ddd" }}>{s.normalized ? s.PI.toFixed(1) : s.PI}</td>
              <td className="p-2 text-center font-display font-bold" style={{ color: highlighted ? "#81C784" : "#ddd" }}>{s.normalized ? s.PR.toFixed(1) : s.PR}</td>
              {pools.length > 1 && <td className="p-2 text-center text-gray-300">{s.poolName?.replace("Pool ", "")}</td>}
            </tr>
            {isCutoffLine && showBracketCutoff && <tr><td colSpan={pools.length > 1 ? 6 : 5} className="p-2 text-center text-xs font-bold text-green-action-text bg-[#1a2a1a]">TOP {bracketInfo.advancing} → ELIMINATION ({bracketInfo.percentage}%)</td></tr>}
            {isAdvancedCutoff && <tr><td colSpan={pools.length > 1 ? 6 : 5} className="p-2 text-center text-xs font-bold text-green-action-text bg-[#1a2a1a]">▲ ADVANCED TO ELIMINATION ({advancingCount})</td></tr>}
          </Fragment>;
        })}</tbody>
      </table>
    </div>
    <div className="flex flex-wrap gap-4 mt-2 px-1 text-xs text-gray-300">
      <span>P/I = Bout Score (W:3 T:1 L:0)</span><span>P/R = Round Score (judge totals)</span>
      {standings.some(s => s.normalized) && <span className="text-yellow-300">* normalized</span>}
    </div>
  </div>;
}
